import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useExerciseCategories } from '../../_contexts/ExerciseCategoriesContext';
import { useTableOfContent } from '../components/TOCDataContext';
import { useFilters } from '../context/filter-context/FilterContext';
import { NodeWithoutDescription } from '../../../../api/table-of-content';
import { ExerciseCategory } from '../../../../api/exercise-category';
import { ResultModule } from '../context/ResultsDataContext';

export type ResolvedTableOfContentNode = {
  prefix?: string;
  title: string;
  id: string;
  nodes: ResolvedTableOfContentNode[];
  image?: string;
};

type ResolvedTableOfContent = ResolvedTableOfContentNode[];

function mapTocNode(
  node: NodeWithoutDescription,
  selectedModule: ResultModule,
  t: TFunction,
): ResolvedTableOfContentNode {
  return {
    prefix: node.displayPrefix ? node.prefix : undefined,
    title: node.id === 'general' ? t('TOC.GENERAL') : node.title,
    id: node.id,
    image: node.id === 'general' ? selectedModule.styling?.logo : node.image,
    nodes: (node.nodes || []).map(n => mapTocNode(n, selectedModule, t)),
  };
}

function mapExerciseCategory(category: ExerciseCategory): ResolvedTableOfContentNode {
  return {
    title: category.name,
    id: category.id,
    nodes: category.categories?.map(mapExerciseCategory) || [],
  };
}

/**
 * Resolves the table of content for the currently filtered module.
 * Can either be a virtual exercise module TOC or a regular module TOC.
 */
export default function useResolvedTableOfContent() {
  const { toc, loading: tocLoading } = useTableOfContent();
  const { exerciseCategories, loading: exerciseCategoriesLoading } = useExerciseCategories();
  const { t } = useTranslation();

  const { selectedModule } = useFilters();

  const resolvedToc: ResolvedTableOfContent = useMemo(() => {
    if (!selectedModule) return [];

    if (selectedModule?.isVirtualExerciseModule) {
      return exerciseCategories.map(mapExerciseCategory);
    }

    return toc.map(node => mapTocNode(node, selectedModule, t));
  }, [toc, exerciseCategories, selectedModule, t]);

  return { toc: resolvedToc, loading: tocLoading || exerciseCategoriesLoading };
}
